import React, { useRef, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  FormControl,
  TextField,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Swal from 'sweetalert2';
import { getSelectedScenarioData, saveCombination } from "../../../Api";

const AddSelectionModal = ({
  summaryData,
  open,
  onClose,
  scenario,
  onScenarioChange,
  startRange,
  onStartRangeChange,
  onSaveSelection,
  scenarios,
  onGenerateSuccess,
  selectedFileType,
  onFileTypeChange
}) => {
  const saveLock = useRef(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingFileName, setIsLoadingFileName] = useState(false);
  
  // Extract unique file types from the API response
  const getUniqueFileTypes = () => {
    if (!summaryData || summaryData.length === 0) return [];
    
    const uniqueFileTypes = [...new Set(summaryData.map(item => item.file_type))];
    return uniqueFileTypes.map((fileType, index) => ({
      id: index + 1,
      file_type: fileType,
      label: fileType // Use the file_type as the label since it comes from API
    }));
  };

  const availableFileTypes = getUniqueFileTypes();
  
  // Handle loading state for file name
  useEffect(() => {
    if (open && (!summaryData || summaryData.length === 0)) {
      setIsLoadingFileName(true);
    } else if (open && summaryData && summaryData.length > 0) {
      setIsLoadingFileName(false);
    }
  }, [open, summaryData]);
  
  

  // Apply selection and get combination_id
  const handleGenerate = async () => {
    if (!scenario || !startRange || !selectedFileType) {
      return;
    }

    setIsGenerating(true);
    try {
      const bank_code = localStorage.getItem("bank_code");
      const correl_id = localStorage.getItem("correl_id");
      
      // Transform the single selection to the format expected by the API
      const range_selection = [{
        scenario: [scenario],
        record_count: parseInt(startRange) || 0
      }];

      // Get the output_filename and processed_id from summary data
      const file_type = selectedFileType;
      const output_filename = summaryData[0]?.output_filename;
      const processed_id = summaryData[0]?.processed_id;

      if (!output_filename || !processed_id) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No processed data found. Please check summary data.',
        });
        return;
      }

      Swal.fire({
        html: `<img src=${require("../../../assets/loader.gif")} style="width: 50px" alt="loader">`,
        showConfirmButton: false,
        heightAuto: false,
        customClass: { popup: "loader-swal" },
        allowOutsideClick: false,
      });

      const response = await saveCombination({
        output_filename,
        file_type,
        scenario,
        record_count: parseInt(startRange) || 0,
        bank_code,
        correl_id,
        processed_id,
      });
      Swal.close();
      if (response && response.status === 'S') {
        const combinationId = response.result_info;
        // Find the correct summary item for this selection
        const summaryItem = summaryData.find(item => item.file_type === selectedFileType && item.scenario === scenario);
        // Use processed_id as unique id if available, else fallback to Date.now()
        const uniqueId = summaryItem?.processed_id || Date.now();
        // Call the success callback with the combination ID and unique id
        onGenerateSuccess(combinationId, {
          id: uniqueId,
          combination_id: combinationId,
          fileName: summaryData[0]?.output_filename || '',
          fileType: selectedFileType || '',
          scenario: scenario || '',
          startRange: startRange,
        });
        // Close the modal
        onClose();
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: response?.message || 'Failed to generate selection',
        });
      }
    } catch (error) {
      console.error('Error generating selection:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error generating selection. Please try again.',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const maxRecordCount = summaryData
  .filter(item => item.file_type === selectedFileType && item.scenario === scenario)
  .reduce((acc, item) => acc + (item.count || 0), 0);

  // Debug log for scenarios and selectedFileType
  console.log('scenarios:', scenarios);
  console.log('selectedFileType:', selectedFileType);

  return (
   <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return;   // Block outside click
        onClose();                                // Allow closing for other reasons
      }}
      maxWidth="xs"
      fullWidth
    >

      <div className='d-flex justify-content-between align-items-center'>
            <DialogTitle sx={{ fontWeight: 600 }}>Add New Selection</DialogTitle>
            <IconButton onClick={onClose} color="black" sx={{ fontWeight: 600 }}>
                <CloseIcon />
              </IconButton>
            </div>
            
      <DialogContent>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>File Name</div>
          <FormControl fullWidth size="small">
            {isLoadingFileName ? (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                backgroundColor: '#f5f5f5',
                minHeight: '40px'
              }}>
                <img 
                  src={require("../../../assets/loader.gif")} 
                  style={{ width: '20px', marginRight: '8px' }} 
                  alt="loading" 
                />
                <span style={{ color: '#666', fontSize: '14px' }}>Loading file name...</span>
              </div>
            ) : (
              <TextField
                value={summaryData[0]?.output_filename || 'No file available'}
                disabled
                fullWidth
                size="small"
              />
            )}
          </FormControl>
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>File Type</div>
          <FormControl fullWidth size="small">
            <Select
              value={selectedFileType || ""}
              onChange={onFileTypeChange}
              displayEmpty
            >
              <MenuItem value="" disabled>
                Select a file type
              </MenuItem>
              {availableFileTypes.map((type) => (
                <MenuItem key={type.id} value={type.file_type}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>Scenario</div>
          <FormControl fullWidth size="small" disabled={!selectedFileType}>
            <Select
              value={selectedFileType ? (scenario || "") : ""}
              onChange={onScenarioChange}
              displayEmpty
              MenuProps={{
                  PaperProps: {
                    style: { maxHeight: 200 }
                  }
                }}
            >
              <MenuItem value="" disabled>
                {selectedFileType ? "Select a scenario" : "Select file type first"}
              </MenuItem>
              {selectedFileType && Array.from(new Set(
                summaryData
                  .filter(item => item.file_type === selectedFileType)
                  .map(item => item.scenario)
              )).map(scenarioOption => (
                <MenuItem key={scenarioOption} value={scenarioOption}>
                  {scenarioOption}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>
            Record Count 
            {/* <span className='themecolor'>{selectedFileType && scenario && (
             (() => {
              const total = summaryData
                .filter(item => item.file_type === selectedFileType && item.scenario === scenario)
                .reduce((acc, item) => acc + (item.count || 0), ""); // Numeric sum

              return total < 10 ? `- 0${total}` : `- ${total}`;
            })()
          )}</span> */}
          {selectedFileType && scenario && (() => {
            const total = summaryData
              .filter(item => item.file_type === selectedFileType && item.scenario === scenario)
              .reduce((acc, item) => acc + (item.count || 0), "");

            if (total > 0) {
              return (
                <>
                  {" - "}
                  <span className="count-color">
                    {total < 10 ? `0${total}` : total}
                  </span>
                </>
              );
            }
            return null;
          })()}
          </div>
            <TextField
              fullWidth
              size="small"
              value={startRange}
              // onChange={e => {
              //   const value = e.target.value.replace(/[^\d]/g, ''); // Only digits
              //   if (value === '' || (Number(value) > 0 && Number(value) <= maxRecordCount)) {
              //     onStartRangeChange(e);
              //   }
              // }}
              onChange={(e) => {
                let rawValue = e.target.value;
                const numericValue = rawValue.replace(/[^\d]/g, ''); // Remove non-digits

                // Allow empty string (for deletion), or valid range
                if (numericValue === '' || (Number(numericValue) > 0 && Number(numericValue) <= maxRecordCount)) {
                  onStartRangeChange({
                    ...e,
                    target: {
                      ...e.target,
                      value: numericValue,
                    },
                  });
                }
              }}
              placeholder="Record Count"
              autoComplete='off'
              helperText={
                !startRange || Number(startRange) < 1 || Number(startRange) > maxRecordCount
                  ? ''
                  : ''
              }
              inputProps={{
                maxLength: 6 // reasonable max length for record count
              }}
            />
        </div>
        {/* {selectedFileType && scenario && (
          <p>
            Total Records: {
              (() => {
                const total = summaryData
                  .filter(item => item.file_type === selectedFileType && item.scenario === scenario)
                  .reduce((acc, item) => acc + (item.count || 0), "");
                return total < 10 ? `0${total}` : `${total}`; // Always two digits
              })()
            }
          </p>
        )} */}
      </DialogContent>
      <div className='d-flex align-items-center justify-content-end p-3 gap-2'>
        <Button onClick={onClose} variant="outlined" className="btn btn-cancel">
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleGenerate}
          variant="contained"
          color="primary"
          disabled={
            !scenario || !startRange || !selectedFileType ||
            Number(startRange) < 1 || Number(startRange) > maxRecordCount || isGenerating
          }
        >
          {isGenerating ? 'Generating...' : 'Generate'}
        </Button>
      </div>
    </Dialog>
  );
};

export default AddSelectionModal;
